import React, { useEffect, useMemo, useState } from 'react';
import {
    CAlert,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CRow,
    CSpinner,
    CTabs,
    CTabList,
    CTab,
    CTabContent,
    CTabPanel,
} from '@coreui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { apiClient } from 'services/Api';
import {
    getChartSplitDefinitions,
    resolveChartSplit,
} from './indicatorChartSplits.js';

const GROUPS = [
    { value: 'MP-01', label: 'Planeamento e Avaliação do Sistema de Gestão (MP-01)' },
    { value: 'MP-02', label: 'Gestão de Processos de Suporte (MP-02)' },
    { value: 'MP-03', label: 'Gestão de Comercial (MP-03)' },
    { value: 'MP-04', label: 'Elaboração e Gestão de Projetos com Incentivos (MP-04)' },
    { value: 'MP-05', label: 'Elaboração e Gestão de Projetos sem Incentivos (MP-05)' },
    { value: 'MP-06', label: 'Gestão da Inovação (MP-06)' },
];

const VALUE_TYPE_LABELS = {
    raw: 'Valor',
    currency: 'Monetário',
    percentage: 'Percentagem',
};

const PT_INT = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 });
const PT_NUM = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 });
const PT_CURRENCY = new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
});

const BASE_COLORS = ['#4f46e5', '#f97316', '#10b981', '#0ea5e9', '#a855f7', '#e11d48'];

function groupLabel(group) {
    return GROUPS.find((g) => g.value === group)?.value || group || '—';
}

function formatValue(valueType, value) {
    if (value === null || value === undefined || value === '') return '—';
    const num = Number(value);
    if (Number.isNaN(num)) return '—';

    if (valueType === 'currency') return PT_CURRENCY.format(num);
    if (valueType === 'percentage') return `${PT_NUM.format(num)}%`;

    return Number.isInteger(num) ? PT_INT.format(num) : PT_NUM.format(num);
}

function lighten(color, amount = 0.25) {
    const clamp = (v) => Math.max(0, Math.min(255, v));
    const hex = color.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const newR = clamp(Math.round(r + (255 - r) * amount));
    const newG = clamp(Math.round(g + (255 - g) * amount));
    const newB = clamp(Math.round(b + (255 - b) * amount));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB
        .toString(16)
        .padStart(2, '0')}`;
}

function buildChartOptions({ title, years, series, valueType }) {
    return {
        chart: {
            height: 360,
            backgroundColor: 'transparent',
            spacing: [12, 12, 12, 12],
        },
        title: { text: title },
        credits: { enabled: false },
        exporting: { enabled: false },
        xAxis: {
            categories: years.map((y) => String(y)),
        },
        yAxis: {
            title: { text: null },
            labels: {
                formatter() {
                    return formatValue(valueType, this.value);
                },
            },
        },
        tooltip: {
            shared: true,
            formatter() {
                const category = this.points?.[0]?.point?.category ?? this.points?.[0]?.key ?? this.x;
                const header = `<b>${category}</b>`;
                const lines = this.points.map(
                    (p) =>
                        `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${formatValue(
                            valueType,
                            p.y,
                        )}</b>`,
                );
                return [header, ...lines].join('<br/>');
            },
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
        },
        plotOptions: {
            column: {
                borderRadius: 4,
                dataLabels: {
                    enabled: false,
                },
            },
            series: {
                marker: {
                    enabled: true,
                    radius: 3,
                },
            },
        },
        series,
    };
}

export default function IndicatorChartsPage() {
    const [loading, setLoading] = useState(false);
    const [indicators, setIndicators] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [activeGroup, setActiveGroup] = useState('');

    useEffect(() => {
        const fetchIndicators = async () => {
            setLoading(true);
            try {
                const response = await apiClient.get('/indicators/charts/');
                const data = response.data || {};
                setIndicators(Array.isArray(data.indicators) ? data.indicators : []);
                setAvailableYears(Array.isArray(data.years) ? data.years : []);
            } catch (error) {
                setIndicators([]);
                setAvailableYears([]);
            } finally {
                setLoading(false);
            }
        };

        fetchIndicators();
    }, []);

    const chartData = useMemo(() => {
        if (!indicators.length) {
            return { years: [], groups: [] };
        }

        const years = (availableYears.length
            ? [...availableYears]
            : Array.from(new Set(indicators.map((i) => i.year)))
        ).sort((a, b) => a - b);
        if (!years.length) {
            return { years: [], groups: [] };
        }

        const lastYears = years.length > 5 ? years.slice(-5) : years;
        const latestYear = years[years.length - 1];
        const latestIndicators = indicators.filter((i) => i.year === latestYear);

        const baseByGroup = new Map();
        const latestLookup = new Map();
        latestIndicators.forEach((ind) => {
            const group = ind.group || '—';
            const valueType = ind.value_type || 'raw';
            if (!baseByGroup.has(group)) baseByGroup.set(group, new Map());
            const typeMap = baseByGroup.get(group);
            if (!typeMap.has(valueType)) typeMap.set(valueType, []);
            typeMap.get(valueType).push({
                description: ind.description,
                value_type: valueType,
            });

            const groupMap = latestLookup.get(group) || new Map();
            groupMap.set(ind.description, ind);
            latestLookup.set(group, groupMap);
        });

        const lookup = new Map();
        indicators.forEach((ind) => {
            if (!lastYears.includes(ind.year)) return;
            const yearMap = lookup.get(ind.year) || new Map();
            const groupMap = yearMap.get(ind.group) || new Map();
            groupMap.set(ind.description, ind);
            yearMap.set(ind.group, groupMap);
            lookup.set(ind.year, yearMap);
        });

        const groups = [];
        baseByGroup.forEach((typeMap, group) => {
            const valueTypes = [];
            typeMap.forEach((baseList, valueType) => {
                const sortedBase = [...baseList].sort((a, b) =>
                    (a.description || '').localeCompare(b.description || ''),
                );
                const splitDefinitions = getChartSplitDefinitions(group, valueType);
                const charts = splitDefinitions?.length
                    ? splitDefinitions.map((definition) => ({
                          key: definition.key,
                          label: definition.label,
                          showObjectiveSeries: definition.showObjectiveSeries ?? true,
                          series: [],
                      }))
                    : [{ key: 'default', label: null, showObjectiveSeries: true, series: [] }];

                sortedBase.forEach((base, idx) => {
                    const objData = lastYears.map((y) => {
                        const row = lookup.get(y)?.get(group)?.get(base.description);
                        return row?.objective ?? null;
                    });
                    const obtData = lastYears.map((y) => {
                        const row = lookup.get(y)?.get(group)?.get(base.description);
                        return row?.obtained ?? null;
                    });

                    const hasValues =
                        objData.some((v) => v !== null && v !== undefined && v !== '') ||
                        obtData.some((v) => v !== null && v !== undefined && v !== '');
                    if (!hasValues) return;

                    const baseColor = BASE_COLORS[idx % BASE_COLORS.length];
                    const lineColor = lighten(baseColor, 0.35);
                    const latestRow = latestLookup.get(group)?.get(base.description) || {};
                    const split = resolveChartSplit(group, valueType, latestRow);
                    const targetChart =
                        charts.find((chart) => chart.key === split.key) || charts[charts.length - 1];

                    if (targetChart.showObjectiveSeries) {
                        targetChart.series.push({
                            name: `Meta ${base.description}`,
                            type: 'spline',
                            data: objData,
                            color: lineColor,
                            marker: { symbol: 'circle' },
                        });
                    }
                    targetChart.series.push({
                        name: base.description,
                        type: 'column',
                        data: obtData,
                        color: baseColor,
                    });
                });

                const populatedCharts = charts.filter((chart) => chart.series.length > 0);
                if (!populatedCharts.length) return;

                valueTypes.push({
                    value_type: valueType,
                    charts: populatedCharts,
                });
            });

            if (!valueTypes.length) return;

            valueTypes.sort((a, b) => (a.value_type || '').localeCompare(b.value_type || ''));
            groups.push({ group, valueTypes });
        });

        const groupOrder = new Map(GROUPS.map((g, idx) => [g.value, idx]));
        groups.sort((a, b) => {
            const aIdx = groupOrder.has(a.group) ? groupOrder.get(a.group) : 999;
            const bIdx = groupOrder.has(b.group) ? groupOrder.get(b.group) : 999;
            if (aIdx !== bIdx) return aIdx - bIdx;
            return (a.group || '').localeCompare(b.group || '');
        });
        return { years: lastYears, groups };
    }, [indicators]);

    useEffect(() => {
        if (!activeGroup && chartData.groups.length) {
            setActiveGroup(chartData.groups[0].group);
        }
    }, [activeGroup, chartData.groups]);

    return (
        <CCard className="mb-4">
            <CCardHeader>
                <h4 className="mb-0">Resultados de Indicadores</h4>
            </CCardHeader>
            <CCardBody>
                {loading ? (
                    <div className="d-flex align-items-center gap-2">
                        <CSpinner size="sm" /> A carregar…
                    </div>
                ) : chartData.groups.length === 0 ? (
                    <CAlert color="info">Sem indicadores disponíveis para gerar gráficos.</CAlert>
                ) : (
                    <CTabs activeItemKey={activeGroup} onChange={setActiveGroup}>
                        <CTabList variant="tabs" className="mb-3">
                            {chartData.groups.map((g) => (
                                <CTab itemKey={g.group} key={g.group}>
                                    {groupLabel(g.group)}
                                </CTab>
                            ))}
                        </CTabList>
                        <CTabContent>
                            {chartData.groups.map((g) => (
                                <CTabPanel itemKey={g.group} key={g.group}>
                                    <CRow className="g-3 mt-1">
                                        {g.valueTypes.flatMap((vt) => {
                                            const label =
                                                VALUE_TYPE_LABELS[vt.value_type] || vt.value_type || 'Valor';

                                            return vt.charts.map((chart) => {
                                                const chartTitle = chart.label
                                                    ? `${label} · ${chart.label}`
                                                    : label;

                                                return (
                                                    <CCol key={`${g.group}-${vt.value_type}-${chart.key}`} lg={12}>
                                                        <CCard className="h-100">
                                                            <CCardHeader>
                                                                <strong>{chartTitle}</strong>
                                                            </CCardHeader>
                                                            <CCardBody>
                                                                <HighchartsReact
                                                                    highcharts={Highcharts}
                                                                    options={buildChartOptions({
                                                                        title: null,
                                                                        years: chartData.years,
                                                                        series: chart.series,
                                                                        valueType: vt.value_type,
                                                                    })}
                                                                />
                                                            </CCardBody>
                                                        </CCard>
                                                    </CCol>
                                                );
                                            });
                                        })}
                                    </CRow>
                                </CTabPanel>
                            ))}
                        </CTabContent>
                    </CTabs>
                )}
            </CCardBody>
        </CCard>
    );
}
